import { useState } from "react";
import { TriangleAlertIcon } from "lucide-react";

import { DetailField, FieldGrid } from "~/components/detail-field";
import { Pill } from "~/components/status-badge";
import { TypedConfirmDialog } from "~/components/typed-confirm-dialog";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Skeleton } from "~/components/ui/skeleton";
import { errorMessage, isNotFound } from "~/lib/api";
import {
  useDeleteShopifyCredentials,
  useDeleteShopifyOAuthCredentials,
  useSetShopifyCredentials,
  useSetShopifyOAuthCredentials,
  useStorefrontShopifyCredentials,
  type ShopifyCredentialsView,
} from "~/lib/queries";

const CONSEQUENCE =
  "Shopify order ingestion for this merchant stops or changes path immediately.";

/**
 * The Shopify tab: attach, replace or remove a merchant's Shopify credentials.
 *
 * Fields here are write-only in both directions. The API never returns a
 * token, client secret or webhook secret -- not masked, not on request -- so
 * there is no reveal control anywhere on this screen, unlike the API key and
 * callback secret tabs. An empty api_version or webhook_secret field on save
 * means "leave the stored value unchanged"; the primary secret (access token,
 * or client id + secret) has to be retyped on every save because the backend
 * has nothing stored to fall back to display or diff against.
 */
export function ShopifyCredentialsTab({
  storefrontId,
  storefrontName,
}: {
  storefrontId: string;
  storefrontName: string;
}) {
  const { data, error, isPending, refetch } = useStorefrontShopifyCredentials(storefrontId);

  if (isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Couldn't load Shopify credentials</CardTitle>
          <CardDescription>
            {isNotFound(error) ? "This storefront no longer exists." : errorMessage(error)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <ActivePathCard view={data} />
      <StaticTokenForm storefrontId={storefrontId} storefrontName={storefrontName} view={data} />
      <OAuthForm storefrontId={storefrontId} storefrontName={storefrontName} view={data} />
    </div>
  );
}

function ActivePathCard({ view }: { view: ShopifyCredentialsView }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          Active path
          <Pill tone={view.credential_type ? "success" : "warning"}>
            {view.credential_type === "oauth"
              ? "OAuth"
              : view.credential_type === "static"
                ? "static token"
                : "env fallback"}
          </Pill>
        </CardTitle>
        <CardDescription>
          {view.credential_type === "oauth" ? (
            "OAuth is active for this storefront. It wins over a static token whenever both are set."
          ) : view.credential_type === "static" ? (
            "A static access token is active for this storefront."
          ) : (
            <>
              <strong className="font-medium text-foreground">
                Neither OAuth nor a static token is configured.
              </strong>{" "}
              Order ingestion, if it happens for this storefront at all, is falling through to
              this server's environment credentials rather than anything set here. That can
              look identical to "working fine" until the environment default changes or is
              removed.
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <FieldGrid>
          <DetailField label="Effective API version" mono>
            {view.effective_api_version}
          </DetailField>
          <DetailField label="Source" className="sm:col-span-2 lg:col-span-2">
            {describeVersionSource(view)}
          </DetailField>
        </FieldGrid>
      </CardContent>
    </Card>
  );
}

function describeVersionSource(view: ShopifyCredentialsView): string {
  switch (view.effective_api_version_source) {
    case "stored":
      return "Stored on this storefront.";
    case "runtime_fallback_2026_01":
      return "No version stored for this storefront — credentials exist, so the code's own default (2026-01) applies.";
    case "env_fallback":
      return `No credentials on this storefront — falling through to this server's SHOPIFY_API_VERSION (${
        view.env_fallback_api_version ?? view.effective_api_version
      }).`;
  }
}

function StaticTokenForm({
  storefrontId,
  storefrontName,
  view,
}: {
  storefrontId: string;
  storefrontName: string;
  view: ShopifyCredentialsView;
}) {
  const [accessToken, setAccessToken] = useState("");
  const [apiVersion, setApiVersion] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [confirmingSet, setConfirmingSet] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const set = useSetShopifyCredentials(storefrontId);
  const remove = useDeleteShopifyCredentials(storefrontId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          Static access token
          {view.static_token_configured ? <Pill tone="success">configured</Pill> : null}
        </CardTitle>
        <CardDescription>
          The legacy path. Loses to OAuth automatically whenever both are set on this
          storefront.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setConfirmingSet(true);
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="static-access-token">Access token</Label>
            <Input
              id="static-access-token"
              type="password"
              value={accessToken}
              onChange={(event) => setAccessToken(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              placeholder={view.static_token_configured ? "Enter to replace the current token" : ""}
              className="font-mono text-[0.8125rem]"
            />
            <p className="text-xs text-muted-foreground">
              Required every time this form is saved — the token is write-only and there is
              nothing stored here to fall back to.
            </p>
          </div>

          <ApiVersionAndWebhookFields
            idPrefix="static"
            apiVersion={apiVersion}
            onApiVersion={setApiVersion}
            webhookSecret={webhookSecret}
            onWebhookSecret={setWebhookSecret}
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" size="sm" disabled={!accessToken.trim() || set.isPending}>
              {set.isPending ? "Saving…" : "Save static token"}
            </Button>
            {view.static_token_configured ? (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={remove.isPending}
                onClick={() => setConfirmingRemove(true)}
              >
                Remove
              </Button>
            ) : null}
          </div>
          {set.error ? <p className="text-xs text-destructive">{errorMessage(set.error)}</p> : null}
        </form>
      </CardContent>

      <TypedConfirmDialog
        open={confirmingSet}
        onOpenChange={setConfirmingSet}
        title={`Set the static Shopify token for ${storefrontName}?`}
        targetLabel="storefront name"
        expectedValue={storefrontName}
        confirmLabel={set.isPending ? "Saving…" : "Save static token"}
        pending={set.isPending}
        consequence={CONSEQUENCE}
        body={
          view.credential_type === "oauth" ? (
            <p className="text-warning">
              This storefront currently uses OAuth. OAuth will keep winning after this save —
              a static token only takes effect once OAuth is removed.
            </p>
          ) : null
        }
        onConfirm={() => {
          set.mutate(
            {
              access_token: accessToken.trim(),
              api_version: apiVersion.trim() || undefined,
              webhook_secret: webhookSecret.trim() || undefined,
            },
            {
              onSuccess: () => {
                setConfirmingSet(false);
                setAccessToken("");
                setApiVersion("");
                setWebhookSecret("");
              },
            },
          );
        }}
      />

      <TypedConfirmDialog
        open={confirmingRemove}
        onOpenChange={setConfirmingRemove}
        title={`Remove the static Shopify token for ${storefrontName}?`}
        targetLabel="storefront name"
        expectedValue={storefrontName}
        confirmLabel={remove.isPending ? "Removing…" : "Remove static token"}
        pending={remove.isPending}
        consequence={CONSEQUENCE}
        onConfirm={() => {
          remove.mutate(undefined, { onSuccess: () => setConfirmingRemove(false) });
        }}
      />
    </Card>
  );
}

function OAuthForm({
  storefrontId,
  storefrontName,
  view,
}: {
  storefrontId: string;
  storefrontName: string;
  view: ShopifyCredentialsView;
}) {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [apiVersion, setApiVersion] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [confirmingSet, setConfirmingSet] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const set = useSetShopifyOAuthCredentials(storefrontId);
  const remove = useDeleteShopifyOAuthCredentials(storefrontId);

  const displacesStaticToken = view.static_token_configured && view.credential_type !== "oauth";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          OAuth credentials
          {view.oauth_configured ? <Pill tone="success">configured</Pill> : null}
        </CardTitle>
        <CardDescription>Wins over a static token automatically once set.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {displacesStaticToken ? (
          <p className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-2.5 text-xs">
            <TriangleAlertIcon className="mt-0.5 size-3.5 shrink-0 text-warning" />
            <span>
              This storefront currently uses its static token. Saving OAuth credentials here
              will make OAuth win instead, effective immediately — nothing is deleted, but the
              static token stops being used.
            </span>
          </p>
        ) : null}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            setConfirmingSet(true);
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="oauth-client-id">Client ID</Label>
            <Input
              id="oauth-client-id"
              type="password"
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              placeholder={view.oauth_configured ? "Enter to replace the current client ID" : ""}
              className="font-mono text-[0.8125rem]"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="oauth-client-secret">Client secret</Label>
            <Input
              id="oauth-client-secret"
              type="password"
              value={clientSecret}
              onChange={(event) => setClientSecret(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              placeholder={view.oauth_configured ? "Enter to replace the current secret" : ""}
              className="font-mono text-[0.8125rem]"
            />
            <p className="text-xs text-muted-foreground">
              Both fields are required every time this form is saved — write-only, nothing
              stored here to fall back to.
            </p>
          </div>

          <ApiVersionAndWebhookFields
            idPrefix="oauth"
            apiVersion={apiVersion}
            onApiVersion={setApiVersion}
            webhookSecret={webhookSecret}
            onWebhookSecret={setWebhookSecret}
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={!clientId.trim() || !clientSecret.trim() || set.isPending}
            >
              {set.isPending ? "Saving…" : "Save OAuth credentials"}
            </Button>
            {view.oauth_configured ? (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={remove.isPending}
                onClick={() => setConfirmingRemove(true)}
              >
                Remove
              </Button>
            ) : null}
          </div>
          {set.error ? <p className="text-xs text-destructive">{errorMessage(set.error)}</p> : null}
        </form>
      </CardContent>

      <TypedConfirmDialog
        open={confirmingSet}
        onOpenChange={setConfirmingSet}
        title={`Set OAuth credentials for ${storefrontName}?`}
        targetLabel="storefront name"
        expectedValue={storefrontName}
        confirmLabel={set.isPending ? "Saving…" : "Save OAuth credentials"}
        pending={set.isPending}
        consequence={CONSEQUENCE}
        body={
          displacesStaticToken ? (
            <p className="text-warning">
              This will make OAuth win over the static token currently in use, immediately.
            </p>
          ) : null
        }
        onConfirm={() => {
          set.mutate(
            {
              client_id: clientId.trim(),
              client_secret: clientSecret.trim(),
              api_version: apiVersion.trim() || undefined,
              webhook_secret: webhookSecret.trim() || undefined,
            },
            {
              onSuccess: () => {
                setConfirmingSet(false);
                setClientId("");
                setClientSecret("");
                setApiVersion("");
                setWebhookSecret("");
              },
            },
          );
        }}
      />

      <TypedConfirmDialog
        open={confirmingRemove}
        onOpenChange={setConfirmingRemove}
        title={`Remove OAuth credentials for ${storefrontName}?`}
        targetLabel="storefront name"
        expectedValue={storefrontName}
        confirmLabel={remove.isPending ? "Removing…" : "Remove OAuth credentials"}
        pending={remove.isPending}
        consequence={CONSEQUENCE}
        body={
          view.static_token_configured ? (
            <p className="text-muted-foreground">
              This storefront also has a static token. Removing OAuth will fall back to it
              immediately.
            </p>
          ) : null
        }
        onConfirm={() => {
          remove.mutate(undefined, { onSuccess: () => setConfirmingRemove(false) });
        }}
      />
    </Card>
  );
}

function ApiVersionAndWebhookFields({
  idPrefix,
  apiVersion,
  onApiVersion,
  webhookSecret,
  onWebhookSecret,
}: {
  idPrefix: string;
  apiVersion: string;
  onApiVersion: (value: string) => void;
  webhookSecret: string;
  onWebhookSecret: (value: string) => void;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-api-version`}>API version</Label>
        <Input
          id={`${idPrefix}-api-version`}
          value={apiVersion}
          onChange={(event) => onApiVersion(event.target.value)}
          placeholder="Leave blank to keep unchanged"
          className="font-mono text-[0.8125rem]"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-webhook-secret`}>Webhook secret</Label>
        <Input
          id={`${idPrefix}-webhook-secret`}
          type="password"
          value={webhookSecret}
          onChange={(event) => onWebhookSecret(event.target.value)}
          placeholder="Leave blank to keep unchanged"
          autoComplete="off"
          className="font-mono text-[0.8125rem]"
        />
        <p className="text-xs text-muted-foreground">
          Verifies Shopify's order webhooks. A wrong value fails that check invisibly — Shopify
          gets no error to show, and this dashboard has no way to detect it from here.
        </p>
      </div>
    </div>
  );
}
